import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useFormikContext } from 'formik';

import type { RootCause } from 'src/types/followUp';

import type { FormFieldKeys } from '../constants';
import FollowUpInput from '../FollowUpInput';

import { styles } from 'src/components/followUp/TicketOverviewPage/TicketDetail/ticketDetail.styles';

interface RootCausesButtonGroupProps {
  disabled?: boolean;
  allRootCauses: Array<RootCause>;
}

const RootCausesButtonGroup: FC<RootCausesButtonGroupProps> = ({
  disabled = false,
  allRootCauses,
}) => {
  const { t } = useTranslation('followUp');

  const { setFieldValue, values, errors, touched } =
    useFormikContext<FormFieldKeys>();
  const filteredRootCauses = useMemo(() => {
    if (disabled)
      return allRootCauses.filter(
        ({ isActive, id }) => isActive || values.rootCauses?.includes(id),
      );

    return allRootCauses.filter(({ isActive }) => isActive);
  }, [disabled, allRootCauses]);

  const handleChange = (_: any, causes: Array<number>) =>
    setFieldValue('rootCauses', causes);

  return (
    <FollowUpInput
      disabled={disabled}
      sx={{ display: values.callHappened ? 'flex' : 'none', gap: 1 }}
      label={t('followUpCall.rootCauses.label')}
      name="rootCausesButtonGroup"
      errorMessage={
        !!errors.rootCauses && !!touched.rootCauses && errors.rootCauses
      }
    >
      <ToggleButtonGroup
        id="rootCausesButtonGroup"
        onChange={handleChange}
        disabled={disabled}
        value={values.rootCauses}
        sx={styles.toggleButtonGroup}
      >
        {filteredRootCauses.map(({ name, id }) => (
          <ToggleButton key={name} sx={styles.toggleButton} value={id}>
            {name}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FollowUpInput>
  );
};

export default RootCausesButtonGroup;
